/* eslint-disable camelcase */
import {
  AgentConnectionResponse,
  ApiError,
  CancelablePromise,
  ChatAgentService,
  CreateAgentParams,
  User,
} from 'api-clients/monolith';
import { OpenAPI } from 'api-clients/monolith/core/OpenAPI';
import { request } from 'api-clients/monolith/core/request';
import { rwxPreviewCredentials } from 'utils/domainMeta';

import { deriveTopicKey } from './ChatbotSetup/KnowledgeBase/v3/topicKey';
import {
  AgentStatus,
  ApplyBotTypes,
  AxAgentResponse,
  AxAgentValues,
  AxThreadResponse,
  FaqEntry,
  KnowledgeBaseJournalEntry,
  KnowledgeBaseJournalResponse,
  KnowledgeBaseResponse,
  ListedAxAgent,
  UploadFaqResponse,
} from './types';

const getBaseUrl = (): string => {
  const url = localStorage.getItem('chatAgentBackendUrl');
  if (!url) {
    throw new Error('Base URL not found in localStorage');
  }
  return url;
};

export type ConversationTurnFilters = Record<string, string | undefined>;
export type ConversationTurnsResponse = Awaited<
  ReturnType<
    typeof ChatAgentService.getInternalApiAgentIntegrationsAgentConversations
  >
>;
export type ConversationTurn = ConversationTurnsResponse['turns'][number];

export const searchConversationOpenings = async (
  accountSlug: string,
  agentId: string,
  q?: string,
) => {
  const response =
    await ChatAgentService.getInternalApiAgentIntegrationsAgentConversationOpenings(
      agentId,
      accountSlug,
      q,
    );
  return {
    openings: response.openings.map(opening => ({
      externalId: opening.external_id,
      title: opening.title,
    })),
    seesAllOpenings: response.sees_all_openings,
  };
};

export type ConversationTranscript = Awaited<
  ReturnType<
    typeof ChatAgentService.getInternalApiAgentIntegrationsAgentConversationThread
  >
>;
export type ConversationTranscriptResult =
  | ConversationTranscript
  | { kind: 'no_access' };

export const fetchConversationTranscript = (
  accountSlug: string,
  agentId: string,
  threadUuid: string,
  turnId?: string,
  opening?: string,
): CancelablePromise<ConversationTranscriptResult> =>
  new CancelablePromise((resolve, reject, onCancel) => {
    const request =
      ChatAgentService.getInternalApiAgentIntegrationsAgentConversationThread(
        agentId,
        threadUuid,
        accountSlug,
        turnId,
        opening,
      );
    // Match the list's browser deadline; a stalled transport must leave loading.
    const timeout = setTimeout(() => {
      request.cancel();
      reject(new Error('Conversation transcript timed out'));
    }, 30000);
    onCancel(() => {
      clearTimeout(timeout);
      request.cancel();
    });
    request
      .then(resolve)
      .catch((error: unknown) => {
        if (error instanceof ApiError && error.status === 403) {
          resolve({ kind: 'no_access' });
        } else {
          reject(error);
        }
      })
      .finally(() => clearTimeout(timeout));
  });

export const fetchConversationTurns = (
  accountSlug: string,
  agentId: string,
  {
    cursor,
    from,
    to,
    ...filters
  }: { cursor?: string } & ConversationTurnFilters,
) => {
  // Extension filters use the same generated transport until the SDK names them.
  if (
    Object.keys(filters).some(
      key =>
        !['opening', 'channel', 'thumbs_down', 'knowledge_gap'].includes(key) &&
        filters[key] !== undefined,
    )
  ) {
    return request<ConversationTurnsResponse>(OpenAPI, {
      method: 'GET',
      url: '/internal_api/agent_integrations/agent/{id}/conversations',
      path: { id: agentId },
      query: { ...filters, account_slug: accountSlug, from, to, cursor },
    });
  }
  if (Object.values(filters).some(value => value !== undefined)) {
    return ChatAgentService.getInternalApiAgentIntegrationsAgentConversations(
      agentId,
      accountSlug,
      from,
      to,
      cursor,
      filters.opening,
      filters.channel as 'widget' | 'sms' | 'whatsapp' | 'email' | undefined,
      filters.thumbs_down === 'true' || undefined,
      filters.knowledge_gap === 'true' || undefined,
    );
  }
  return ChatAgentService.getInternalApiAgentIntegrationsAgentConversations(
    agentId,
    accountSlug,
    from,
    to,
    cursor,
  );
};

async function fetchJson<T>(url: RequestInfo, data?: RequestInit): Promise<T> {
  const credentials =
    typeof url === 'string' ? rwxPreviewCredentials(url) : undefined;
  const response = await fetch(url, { credentials, ...data });
  if (!response.ok) {
    throw response;
  }
  return response.json() as Promise<T>;
}

export const fetchAccessToken = async (
  accountSlug: string,
): Promise<{
  access_token: string;
  chat_agent_connection_url: string;
  chat_agent_widget_url: string;
}> => {
  const response =
    await ChatAgentService.getInternalApiAgentIntegrationsAgentFetchAccessToken(
      accountSlug,
    );

  return response as {
    access_token: string;
    chat_agent_connection_url: string;
    chat_agent_widget_url: string;
  };
};

export const createAgent = async (
  accountSlug: string,
  name: string,
): Promise<{ agent_id: string }> => {
  const response = await ChatAgentService.postInternalApiAgentIntegrationsAgent(
    accountSlug,
    {
      name,
    } as CreateAgentParams,
  );

  return response as { agent_id: string };
};

export const createRxAgent = async (
  accountSlug: string,
): Promise<{ rx_agent_id: string }> => {
  const response =
    await ChatAgentService.postInternalApiAgentIntegrationsAgentCreateRxAgent(
      accountSlug,
    );

  return response as { rx_agent_id: string };
};

export const createRxThreadAndSignature = async (
  accountSlug: string,
  axAgentId: string,
  rxAgentId: string,
  knowledgeBaseId: string,
): Promise<{
  expiration: string;
  signature: string;
  tenant: string;
  thread_id: string;
}> => {
  const response =
    await ChatAgentService.postInternalApiAgentIntegrationsAgentCreateRxThreadAndSignature(
      accountSlug,
      axAgentId,
      rxAgentId,
      knowledgeBaseId,
    );

  return response as {
    expiration: string;
    signature: string;
    tenant: string;
    thread_id: string;
  };
};

export const deleteAgent = async (
  accountSlug: string,
  agentId: string,
): Promise<unknown> => {
  const response =
    await ChatAgentService.deleteInternalApiAgentIntegrationsAgent(
      accountSlug,
      agentId,
    );
  return response;
};

export const retrieveAgentConnection = async (
  accountSlug: string,
  agentId: string,
): Promise<AgentConnectionResponse> => {
  const response = await ChatAgentService.getInternalApiAgentIntegrationsAgent(
    accountSlug,
    agentId,
  );
  return response;
};

export interface AgentSetupList {
  sampleKnowledgeBaseFileUrl: string;
  knowledgeBaseHelpCenterArticleUrl: string;
  availableAgents: AxAgentValues['availableAgents'];
  agents: ListedAxAgent[];
  // Hire could not read the list from fountain-ai, so `agents` is incomplete.
  agentsIncomplete: boolean;
}

// Hire reads the list from fountain-ai and keeps the tenant token server-side.
export const fetchAgentSetupList = async (
  accountSlug: string,
): Promise<AgentSetupList> => {
  const response =
    await ChatAgentService.getInternalApiAgentIntegrationsAgent1(accountSlug);
  return {
    sampleKnowledgeBaseFileUrl: response.sample_knowledge_base_file_url,
    knowledgeBaseHelpCenterArticleUrl:
      response.knowledge_base_help_center_article_url,
    availableAgents: response.available_agents,
    // A Hire that predates this field answers without it; hire deploys first.
    agents: (response.agents ?? []).map(agent => ({
      uuid: agent.uuid,
      name: agent.name,
      status: agent.status,
      isDeleted: agent.is_deleted,
      createdAt: agent.created_at,
      updatedAt: agent.updated_at,
      lastModifiedBy: agent.last_modified_by,
      modifiedByName: agent.modified_by_name,
      knowledgeBaseUuid: agent.knowledge_base_uuid,
    })),
    agentsIncomplete: !response.agents || response.agents_error != null,
  };
};

export const PublishChatAgent = async ({
  accountSlug,
  brandId,
  enableInFountainPlatforms,
  smsEnabled,
  agentId,
  status,
}: {
  accountSlug: string;
  brandId?: string | null;
  enableInFountainPlatforms?: boolean;
  smsEnabled?: boolean;
  agentId?: string;
  status?: AgentStatus;
}): Promise<unknown> => {
  const response =
    await ChatAgentService.postInternalApiAgentIntegrationsAgentPublishChatAgent(
      accountSlug,
      brandId as string,
      enableInFountainPlatforms,
      smsEnabled,
      agentId,
      status,
    );
  return response;
};

export const updateAgentConnection = async (
  accountSlug: string,
  agentId: string,
  enableInFountainPlatforms?: boolean,
  smsEnabled?: boolean,
  brandId?: string,
): Promise<AgentConnectionResponse> => {
  const response = await ChatAgentService.putInternalApiAgentIntegrationsAgent(
    accountSlug,
    agentId,
    {
      enable_in_fountain_platforms: enableInFountainPlatforms,
      sms_to_chat_agent_enabled: smsEnabled,
    },
    brandId,
  );
  return response;
};

export const fetchUser = async (
  accountSlug: string,
  userExternalId: string,
): Promise<{ user: User }> => {
  const response =
    await ChatAgentService.getInternalApiAgentIntegrationsAgentUser(
      accountSlug,
      userExternalId,
    );
  return response;
};

export const createKnowledgeBase = async (
  accessToken: string,
  version?: string,
): Promise<KnowledgeBaseResponse> => {
  const response = await fetchJson<KnowledgeBaseResponse>(
    `${getBaseUrl()}/api/v1/knowledge-base`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ name: 'knowledge-base', version }),
    },
  );
  return response;
};

export const retrieveKnowledgeBase = async (
  accessToken: string,
  knowledgeBaseUuid: string,
): Promise<KnowledgeBaseResponse> => {
  const response = await fetchJson<KnowledgeBaseResponse>(
    `${getBaseUrl()}/api/v1/knowledge-base/${knowledgeBaseUuid}`,
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    },
  );
  return response;
};

export const uploadFaq = async (
  accessToken: string,
  knowledgeBaseUuid: string,
  file: File,
  userExternalId: string,
): Promise<UploadFaqResponse> => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('createdBy', userExternalId);

  const response = await fetchJson<UploadFaqResponse>(
    `${getBaseUrl()}/api/v1/knowledge-base/${knowledgeBaseUuid}/faq`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      body: formData,
    },
  );
  return response;
};

export const downloadFaq = async (
  accessToken: string,
  knowledgeBaseUuid: string,
): Promise<Blob> => {
  const response = await fetch(
    `${getBaseUrl()}/api/v1/knowledge-base/${knowledgeBaseUuid}/faq`,
    {
      credentials: rwxPreviewCredentials(getBaseUrl()),
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    },
  );
  if (!response.ok) {
    throw new Error('Failed to download FAQ');
  }
  return response.blob();
};

export const retrieveAxAgent = async (
  accessToken: string,
  agentId: string,
): Promise<AxAgentResponse> => {
  const response = await fetchJson<AxAgentResponse>(
    `${getBaseUrl()}/api/v1/ax-agent/${agentId}`,
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    },
  );
  return response;
};

export const updateAxAgent = async (
  accessToken: string,
  axAgentUuid: string,
  data: {
    name?: string;
    hasHumanFallback?: boolean;
    emailFallback?: string;
    phoneNumberFallback?: string;
    websiteFallback?: string;
    knowledgeBaseUuid?: string;
    brand?: string;
    status?: AgentStatus;
    allowedOrigins?: string[] | null;
    applyBotType?: ApplyBotTypes;
    lastModifiedBy?: string;
    careerSiteUrl?: string;
    languageCode?: string;
  },
): Promise<AxAgentResponse> => {
  const response = await fetchJson<AxAgentResponse>(
    `${getBaseUrl()}/api/v1/ax-agent/${axAgentUuid}`,
    {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify(data),
    },
  );
  return response;
};

export interface FileSource {
  uuid: string;
  filename: string;
  size: number;
  status: string;
  createdAt: string;
}

export const uploadFileSource = async (
  accessToken: string,
  knowledgeBaseUuid: string,
  file: File,
): Promise<FileSource> => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await fetchJson<FileSource>(
    `${getBaseUrl()}/api/v1/knowledge-base/${knowledgeBaseUuid}/file-source`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      body: formData,
    },
  );
  return response;
};

// Triggers the bootstrap LLM pipeline to extract content into KB topics
export const triggerBootstrap = async (
  accessToken: string,
  knowledgeBaseUuid: string,
  file: File,
  agentUuid?: string,
): Promise<void> => {
  const formData = new FormData();
  formData.append('file', file);
  if (agentUuid) formData.append('agentUuid', agentUuid);

  const response = await fetch(
    `${getBaseUrl()}/api/v1/knowledge-base/${knowledgeBaseUuid}/bootstrap/recruiter`,
    {
      credentials: rwxPreviewCredentials(getBaseUrl()),
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      body: formData,
    },
  );

  if (!response.ok) {
    throw new Error(
      `triggerBootstrap failed: ${response.status} ${response.statusText}`,
    );
  }
};

export type BootstrapRunStatus =
  | 'in_progress'
  | 'completed'
  | 'failed'
  | 'ready';

export interface LatestBootstrapStatus {
  uuid: string;
  runStatus: BootstrapRunStatus;
}

export const getLatestBootstrapStatus = async (
  accessToken: string,
  knowledgeBaseUuid: string,
): Promise<LatestBootstrapStatus> => {
  const response = await fetchJson<LatestBootstrapStatus>(
    `${getBaseUrl()}/api/v1/knowledge-base/${knowledgeBaseUuid}/bootstrap/latest`,
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    },
  );
  return response;
};

export const listFileSources = async (
  accessToken: string,
  knowledgeBaseUuid: string,
): Promise<FileSource[]> => {
  const response = await fetchJson<FileSource[]>(
    `${getBaseUrl()}/api/v1/knowledge-base/${knowledgeBaseUuid}/file-source`,
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    },
  );
  return response;
};

export const deleteFileSource = async (
  accessToken: string,
  knowledgeBaseUuid: string,
  fileSourceUuid: string,
): Promise<void> => {
  await fetch(
    `${getBaseUrl()}/api/v1/knowledge-base/${knowledgeBaseUuid}/file-source/${fileSourceUuid}`,
    {
      credentials: rwxPreviewCredentials(getBaseUrl()),
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    },
  );
};

export const createFaqSection = async (
  accessToken: string,
  knowledgeBaseUuid: string,
  question: string,
  answer: string,
  recruiterId?: string,
): Promise<FaqEntry> => {
  const response = await fetchJson<FaqEntry>(
    `${getBaseUrl()}/api/v1/knowledge-base/${knowledgeBaseUuid}/faq-section`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      // topicKey engages the backend merge/upsert so a later bootstrap upload
      // lands on this row instead of inserting a duplicate (HRAI-2022).
      body: JSON.stringify({
        question,
        answer,
        topicKey: deriveTopicKey(question),
        createdBy: recruiterId,
      }),
    },
  );
  return response;
};

export const updateFaqSection = async (
  accessToken: string,
  knowledgeBaseUuid: string,
  faqUuid: string,
  data: { question?: string; answer?: string },
  recruiterId?: string,
): Promise<FaqEntry> => {
  const response = await fetchJson<FaqEntry>(
    `${getBaseUrl()}/api/v1/knowledge-base/${knowledgeBaseUuid}/faq-section/${faqUuid}`,
    {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      // A rename recomputes topic_key, or the row would keep its old identity
      // and a later upload would re-create the old topic beside it.
      body: JSON.stringify({
        ...data,
        ...(data.question !== undefined
          ? { topicKey: deriveTopicKey(data.question) }
          : {}),
        createdBy: recruiterId,
      }),
    },
  );
  return response;
};

export const deleteFaqSection = async (
  accessToken: string,
  knowledgeBaseUuid: string,
  faqUuid: string,
  recruiterId?: string,
): Promise<void> => {
  await fetch(
    `${getBaseUrl()}/api/v1/knowledge-base/${knowledgeBaseUuid}/faq-section/${faqUuid}`,
    {
      credentials: rwxPreviewCredentials(getBaseUrl()),
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ createdBy: recruiterId }),
    },
  );
};

export const fetchKnowledgeBaseJournal = (
  accessToken: string,
  knowledgeBaseUuid: string,
): Promise<KnowledgeBaseJournalResponse> =>
  fetchJson<KnowledgeBaseJournalResponse>(
    `${getBaseUrl()}/api/v1/knowledge-base/${knowledgeBaseUuid}/journal`,
    { headers: { Authorization: `Bearer ${accessToken}` } },
  );

export const restoreKnowledgeBaseJournalEntry = async (
  accessToken: string,
  knowledgeBaseUuid: string,
  journalUuid: string,
  createdBy?: string,
): Promise<KnowledgeBaseJournalEntry> => {
  return fetchJson<KnowledgeBaseJournalEntry>(
    `${getBaseUrl()}/api/v1/knowledge-base/${knowledgeBaseUuid}/journal/${journalUuid}/restore`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ createdBy }),
    },
  );
};

export const updateAtsConnection = async (
  accessToken: string,
  atsConnectionId: string,
  brandName: string,
  brandId: string | null,
  accountSlug: string,
): Promise<unknown> => {
  const response = await fetchJson<AxThreadResponse>(
    `${getBaseUrl()}/api/v1/ats/ats_connection/${atsConnectionId}`,
    {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        name: brandName,
        extra: { brandId, accountSlug },
      }),
    },
  );
  return response;
};
